import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const defaultProducts = [
    {
      id: '1',
      name: 'Portable 3 kW Charger',
      category: 'Portable Chargers',
      power_rating: '3 kW',
      connector_type: 'Type 2',
      charging_speed: '15-20 km per hour',
      features: ['Compact design', 'SA 3-pin compatible', 'Adjustable charging speed', 'Weatherproof'],
      applications: 'Ideal for overnight home charging and emergency backup',
      image_url: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&h=600&fit=crop'
    },
    {
      id: '2',
      name: 'Portable 7 kW Charger',
      category: 'Portable Chargers',
      power_rating: '7 kW',
      connector_type: 'Type 2',
      charging_speed: '35-45 km per hour',
      features: ['Fast charging', 'Industrial socket support', 'LCD display', 'Portable case included'],
      applications: 'Perfect for home and workplace charging',
      image_url: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&h=600&fit=crop'
    },
    {
      id: '3',
      name: 'Wall-Mounted Smart Charger',
      category: 'Smart Wall Chargers',
      power_rating: '7-22 kW',
      connector_type: 'Type 2',
      charging_speed: '35-120 km per hour',
      features: ['WiFi connectivity', 'Load management', 'RFID access', 'Solar compatible'],
      applications: 'Residential and small business installations',
      image_url: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&h=600&fit=crop'
    },
    {
      id: '4',
      name: 'Commercial AC Charger',
      category: 'Commercial Chargers',
      power_rating: '22 kW',
      connector_type: 'Type 2 / CCS2',
      charging_speed: '100-120 km per hour',
      features: ['Dual sockets', 'Payment integration', 'Remote monitoring', 'Vandal-proof design'],
      applications: 'Shopping centers, office parks, public parking',
      image_url: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&h=600&fit=crop'
    },
    {
      id: '5',
      name: 'DC Fast Charger',
      category: 'Commercial Chargers',
      power_rating: '60-350 kW',
      connector_type: 'CCS2 / CHAdeMO',
      charging_speed: '300-500 km in 15-30 min',
      features: ['Ultra-fast charging', 'Multi-standard support', 'Digital signage', 'Cloud management'],
      applications: 'Highway rest stops, fuel stations, fleet depots',
      image_url: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&h=600&fit=crop'
    }
  ];

  const displayProducts = products.length > 0 ? products : defaultProducts;

  return (
    <div className="min-h-screen">
      <section
        data-testid="products-hero-section"
        className="relative py-32 bg-gradient-to-br from-[#0A3D2E] to-[#07291F] text-white"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter mb-6 font-['Outfit']">
              EV Charging Products
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Premium charging solutions for every need and budget.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="products-grid-section" className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-4 font-['Outfit']">
              Our EV Chargers
            </h2>
            <p className="text-lg text-[#4A5D54] max-w-2xl mx-auto">
              Professional portable charging solutions with smart scheduling features for home and workplace use.
            </p>
          </motion.div>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#4A5D54]">Loading products...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 justify-items-center">
              {displayProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`product-card-${index}`}
                  className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all w-full max-w-md"
                >
                  <div className="aspect-video bg-[#F4F7F5] relative overflow-hidden">
                    <img
                      src={product.image_url || 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&h=600&fit=crop'}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-[#0A3D2E] text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {product.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#0A3D2E] mb-2 font-['Outfit']">
                      {product.name}
                    </h3>
                    <div className="space-y-2 mb-4 text-sm text-[#4A5D54]">
                      <p><strong>Power:</strong> {product.power_rating}</p>
                      <p><strong>Connector:</strong> {product.connector_type}</p>
                    </div>
                    <p className="text-sm text-[#4A5D54] mb-4">{product.applications}</p>
                    <ul className="space-y-1 mb-6">
                      {product.features.slice(0, 4).map((feature) => (
                        <li key={feature} className="flex items-center text-sm text-[#4A5D54]">
                          <Zap size={14} className="text-[#CDFF00] mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      data-testid={`product-request-pricing-${index}`}
                      className="block w-full text-center px-6 py-3 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                    >
                      Request Pricing
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section data-testid="products-cta-section" className="py-24 bg-[#F4F7F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-6 font-['Outfit']">
              Need Help Choosing?
            </h2>
            <p className="text-lg text-[#4A5D54] mb-8">
              Our team can recommend the perfect charging solution for your needs.
            </p>
            <Link
              to="/contact"
              data-testid="products-contact-button"
              className="inline-flex items-center px-8 py-4 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none"
            >
              Contact Our Team <ChevronRight size={20} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
